<i18n>
{
	"en": {
		"customerName": "Customer",
		"eta": "ETA/ATA",
		"etd": "ETD/ATD",
		"from": "From",
		"loadingMeters": "Loading",
		"numberOfPallets": "Pallets",
		"order": "Order",
		"orderType": "Order Type",
		"status": "Status",
		"to": "To",
		"volume": "Volume",
		"chargableWeight": "Chargable Weight",
		"waybillNumber": "Waybill no.",
		"registeredDate": "Registered Date",
		"numberOfPackages": "Packages",
		"consignee": "Consignee",
		"consignor": "Consignor",
		"accepted": "Accepted",
		"grossWeight": "Gr. Weight",
		"consigneeReference": "Consignee Ref",
		"customersReference": "Customers Ref",
		"internalNotes": "Internal Notes",
		"poReference": "PO Ref",
		"sendersReference": "Senders Ref",
		"transporterInstructions": "Transporter Instructions",
		"despatchParty": "Despatch Party",
		"deliveryParty": "Delivery Party",
		"yes": "Yes",
		"no": "No",
		"editOrder": "Edit Order",
		"geocodeOrder": "Geocode Order",
		"packageLabel": "Labels",
		"waybill": "Waybill",
		"status/cancelled": "Cancelled",
		"status/draft": "Draft",
		"status/ready": "Ready",
		"status/planned": "Planned",
		"status/rejected": "Rejected",
		"status/unsuccessful": "Unsuccessful",
		"status/requested": "Requested",
		"status/received": "Received",
		"status/accepted": "Accepted",
		"status/readyForPickup": "Ready for Pickup",
		"status/loaded": "Loaded",
		"status/unloaded": "Unloaded",
		"parallelSuborder": "Create Parallel Suborder"
	},
	"no": {
		"customerName": "Kunde",
		"eta": "ETA/ATA",
		"etd": "ETD/ATD",
		"from": "Fra",
		"loadingMeters": "Lastm.",
		"numberOfPallets": "Paller",
		"order": "Oppdrag",
		"orderType": "Oppdragstype",
		"status": "Status",
		"to": "Til",
		"volume": "Vol",
		"waybillNumber": "Fraktbrevnr",
		"chargableWeight": "Fraktberegningsvekt",
		"registeredDate": "Registrert dato",
		"numberOfPackages": "Kolli",
		"consignee": "Mottaker",
		"consignor": "Avsender",
		"accepted": "Akseptert",
		"grossWeight": "Br. vekt",
		"consigneeReference": "Mottaker Ref",
		"customersReference": "Kunde Ref",
		"internalNotes": "Interne notater",
		"poReference": "PO ref",
		"sendersReference": "Avsender ref",
		"transporterInstructions": "Transportør instruksjoner",
		"economicalStatus": "Økonomisk status",
		"despatchParty": "Henteaddresse",
		"deliveryParty": "Leveringsaddresse",
		"yes": "Ja",
		"no": "Nei",
		"editOrder": "Rediger oppdrag",
		"geocodeOrder": "Geokode oppdrag",
		"packageLabel": "Etiketter",
		"waybill": "Fraktbrev",
		"status/cancelled": "Annullert",
		"status/draft": "Kladd",
		"status/ready": "Klar",
		"status/planned": "Planlagt",
		"status/rejected": "Avslått",
		"status/unsuccessful": "Bomtur",
		"status/requested": "Forespurt",
		"status/received": "Mottatt",
		"status/accepted": "Akseptert",
		"status/readyForPickup": "Klar for henting",
		"status/loaded": "Lastet",
		"status/unloaded": "Losset",
		"parallelSuborder": "Lag parallell strekning"
	}
}
</i18n>
<template>
	<div style="height:100%;width:100%">
		<t-grid
			ref="grid"
			:draggable="true"
			:columnDefinitions="columnDefinitions"
			:defaultColumnDefinition="defaultColumnDefinition"
			:rowHeight="50"
			:items="orders"
			:localStorageId="localStorageId"
			selectable
			@selected="$emit('selected', $event)"
			@rowDoubleClick="setEditOrder"
			@enter="setEditOrder"
			@filtered="$emit('filtered', $event)"
		>
			<template v-slot:status="{ item }">
				<t-timpex-icon
					:icon="getStatusIcon(item)"
					:color="getStatusColor(item)"
					:title="getStatusTitle(item)"
					size="md"
					style="padding-left: 3px;"
				/>
			</template>
			<template v-slot:from="{ displayValue }">
				<div v-if="displayValue" style="height: 16px;">{{ displayValue.name }}</div>
				<div v-if="displayValue" class="grid-cell-sub-line" :class="{'missing-geo-data': !displayValue.latitude || !displayValue.longitude }">{{ displayValue.street }}, {{ displayValue.postalCode }} {{ displayValue.postalPlace }}</div>
			</template>
			<template v-slot:to="{ displayValue }">
				<div v-if="displayValue" style="height: 16px;">{{ displayValue.name }}</div>
				<div v-if="displayValue" class="grid-cell-sub-line" :class="{'missing-geo-data': !displayValue.latitude || !displayValue.longitude }">{{ displayValue.street }}, {{ displayValue.postalCode }} {{ displayValue.postalPlace }}</div>
			</template>
			<template v-slot:despatchParty="{ item }">
				<div v-if="item.despatchParty" style="height: 16px;">{{ item.despatchParty.name }}</div>
				<div v-if="item.despatchParty" class="grid-cell-sub-line" :class="{'missing-geo-data': !item.despatchParty.latitude || !item.despatchParty.longitude }">{{ item.despatchParty.street }}, {{ item.despatchParty.postalCode }} {{ item.despatchParty.postalPlace }}</div>
			</template>
			<template v-slot:deliveryParty="{ item }">
				<div v-if="item.deliveryParty" style="height: 16px;">{{ item.deliveryParty.name }}</div>
				<div v-if="item.deliveryParty" class="grid-cell-sub-line" :class="{'missing-geo-data': !item.deliveryParty.latitude || !item.deliveryParty.longitude }">{{ item.deliveryParty.street }}, {{ item.deliveryParty.postalCode }} {{ item.deliveryParty.postalPlace }}</div>
			</template>
			<template v-slot:consignee="{ item }">
				<div v-if="item.consignee" style="height: 16px;">{{ item.consignee.name }}</div>
				<div v-if="item.consignee" class="grid-cell-sub-line">{{ item.consignee.street }}, {{ item.consignee.postalCode }} {{ item.consignee.postalPlace }}</div>
			</template>
			<template v-slot:consignor="{ item }">
				<div v-if="item.consignor" style="height: 16px;">{{ item.consignor.name }}</div>
				<div v-if="item.consignor" class="grid-cell-sub-line">{{ item.consignor.street }}, {{ item.consignor.postalCode }} {{ item.consignor.postalPlace }}</div>
			</template>
			<template v-slot:customer="{ item }">
				<div v-if="item.customer" style="height: 16px;">{{ item.customer.name }}</div>
				<div v-if="item.customer" class="grid-cell-sub-line">{{ item.customer.customerNo }}</div>
			</template>
			<template v-slot:appMessages="{ value }">
				<t-icon-button
					v-if="value && value.length > 0"
					:value="value[value.length - 1].message"
					icon="chat"
					color="blue"
					iconSize="xs"
					appearance="minimal"
					type="bp"
					@click="openViewAppMessages(value)"
				/>
			</template>
			<template v-slot:contextMenu="{ item }">
				<t-menu>
					<t-menu-group>
						<t-menu-item icon="edit" @click="setEditOrder(item.id)"><strong>{{$t('editOrder')}}</strong></t-menu-item>
						<t-menu-item icon="locate" @click="$emit('geocode', item)">{{$t('geocodeOrder')}}</t-menu-item>
						<t-menu-item icon="plus" @click="$emit('parallelSuborder', item.id)">{{$t('parallelSuborder')}}</t-menu-item>
						<hr>
						<t-menu-item
							icon="package-label"
							@click="$emit('packageLabel', item.orderId)"
						>
							{{ $t('packageLabel') }}
						</t-menu-item>

						<t-menu-item
							icon="waybill"
							@click="$emit('waybill', item.orderId)"
						>
							{{ $t('waybill') }}
						</t-menu-item>
					</t-menu-group>
				</t-menu>
			</template>
		</t-grid>
		<AppMessages
			:isShown="showAppMessages"
			:appMessages="appMessages"
			@close="showAppMessages = false"
		/>
	</div>
</template>

<script>
import Grid from '@/components/evergreen/timpex/grid/TGrid';
import AppMessages from './modals/AppMessages';

import {
	displayDateFormat,
	storageDateFormat,
	displayDateFormatter,
	dateExists,
	goodsTypesIcons,
	statuses,
	eventBus
} from '@/core';
import moment from 'moment';
import OrderStatusFilter from '@/modules/orderSearch/OrderStatusFilter';
import TMenuItem from '@/components/evergreen/menu/TMenuItem';

let gridfields = {
	beforeMount() {
		this.columnDefinitions = [
			{
				field: 'status',
				displayName: this.$t('status'),
				valueGetter: (item) => item.name,
				width: 50,
				resizable: false,
				customFilterComponent: OrderStatusFilter,
				headerFilterButtonOnly: true,
			},
			{
				field: 'etd.date',
				displayName: this.$t('etd'),
				valueGetter: (item) => displayDateFormatter(this.dateGetter(item.atd, item.etd)),
				width: 90,
				resizable: false,
				type: 'date',
				dateFormat: displayDateFormat,
				comparableValueGetter: (item) => moment(this.dateGetter(item.atd, item.etd), storageDateFormat).startOf('day').toISOString(),
				cellClass(item) { return dateExists(item.atd) ? 'grid-cell-bold' : 'grid-cell-light'; }
			},
			{
				field: 'from',
				displayName: this.$t('from'),
				valueGetter: item => item.despatchParty || item.consignor,
				comparableValueGetter: item => this.addressGetter(item.despatchParty || item.consignor),
				type: 'address',
				minWidth: 125,
			},
			{
				field: 'to',
				displayName: this.$t('to'),
				valueGetter: item => item.deliveryParty || item.consignee,
				comparableValueGetter: item => this.addressGetter(item.deliveryParty || item.consignee),
				type: 'address',
				minWidth: 125,
			},
			{
				field: 'eta.date',
				displayName: this.$t('eta'),
				valueGetter: (item) => displayDateFormatter(this.dateGetter(item.ata, item.eta)),
				width: 90,
				resizable: false,
				type: 'date',
				dateFormat: displayDateFormat,
				comparableValueGetter: (item) => moment(this.dateGetter(item.ata, item.eta), storageDateFormat).startOf('day').toISOString(),
				cellClass(item) { return dateExists(item.ata) ? 'grid-cell-bold' : 'grid-cell-light'; }
			},
			{
				field: 'customer',
				displayName: this.$t('customerName'),
				comparableValueGetter: ({ customer }) => customer ? `${customer.name} ${customer.customerNo}` : '',
				minWidth: 125,
				type: 'customer',
			},
			{
				field: 'orderNumber',
				displayName: this.$t('order'),
				width: 90,
				resizable: false,
				comparableValueGetter: (item) => Number(item.orderNumber),
				type: 'number',
				sortDirection: 'desc'
			},
			{
				field: 'orderType',
				displayName: this.$t('orderType'),
				valueGetter: ({ orderType }) => orderType ? orderType.name : '',
				minWidth: 100,
			},
			{
				field: 'numberOfPackages',
				displayName: this.$t('numberOfPackages'),
				type: 'number',
				width: 60
			},
			{
				field: 'loadingMeters',
				displayName: this.$t('loadingMeters'),
				type: 'number',
				width: 60,
			},
			{
				field: 'volume',
				displayName: this.$t('volume'),
				type: 'number',
				width: 60
			},
			{
				field: 'grossWeight',
				displayName: this.$t('grossWeight'),
				type: 'number',
				width: 60
			},
			{
				field: 'chargableWeight',
				displayName: this.$t('chargableWeight'),
				type: 'number',
				width: 60
			},
			{
				field: 'waybillNumber',
				displayName: this.$t('waybillNumber'),
				width: 100,
				resizable: false,
				type: 'number',
			},
			{
				field: 'registeredDate',
				displayName: this.$t('registeredDate'),
				valueGetter: (item) => displayDateFormatter(this.singleDateGetter(item.registeredDate)),
				type: 'date',
				dateFormat: displayDateFormat,
				comparableValueGetter: (item) => moment(this.singleDateGetter(item.registeredDate), storageDateFormat).startOf('day').toISOString()
			},
			{
				field: 'numberOfPallets',
				displayName: this.$t('numberOfPallets'),
				type: 'number',
				width: 60,
			},
			{
				field: 'despatchParty',
				displayName: this.$t('despatchParty'),
				comparableValueGetter: item => this.addressGetter(item.despatchParty),
				type: 'address',
				minWidth: 125,
			},
			{
				field: 'deliveryParty',
				displayName: this.$t('deliveryParty'),
				comparableValueGetter: item => this.addressGetter(item.deliveryParty),
				type: 'address',
				minWidth: 125,
			},
			{
				field: 'consignee',
				displayName: this.$t('consignee'),
				comparableValueGetter: item => this.addressGetter(item.consignee),
				type: 'address',
				minWidth: 125,
			},
			{
				field: 'consignor',
				displayName: this.$t('consignor'),
				comparableValueGetter: item => this.addressGetter(item.consignor),
				type: 'address',
				minWidth: 125,
			},
			{
				field: 'accepted',
				displayName: this.$t('accepted'),
				valueGetter: item => item.fullyPlanned ? this.$t('yes') : this.$t('no'),
			},
			{ field: 'consigneeReference', displayName: this.$t('consigneeReference') },
			{ field: 'customersReference', displayName: this.$t('customersReference') },
			{ field: 'internalNotes', displayName: this.$t('internalNotes') },
			{ field: 'poReference', displayName: this.$t('poReference') },
			{ field: 'sendersReference', displayName: this.$t('sendersReference') },
			{
				field: 'transporterInstructions',
				displayName: this.$t('transporterInstructions'),
				minWidth: 125,
			},
			{
				field: 'appMessages',
				displayName: '',
				width: 45,
				resizable: false
			}
		];
	},
};


export default {
	name: 'OrderGrid',
	components: { TMenuItem, TGrid: Grid, AppMessages },
	mixins: [gridfields],
	props: {
		orders: Array,
		localStorageId: String,
	},
	data() {
		return {
			defaultColumnDefinition: {
				resizable: true,
				headerFilterInputWithButton: true,
			},
			columnDefinitions: [],
			appMessages: [],
			showAppMessages: false
		};
	},
	methods: {
		addressGetter(party) {
			if (!party) return '';
			return `${party.name} ${party.street} ${party.postalCode} ${party.postalPlace}`;
		},
		dateGetter(actual, estimated) {
			if (dateExists(actual)) return actual.date;
			if (dateExists(estimated)) return estimated.date;
			return undefined;
		},
		singleDateGetter(dateObj) {
			return dateExists(dateObj) ? dateObj.date : undefined;
		},
		getStatusIcon(item) {
			return goodsTypesIcons(item.goodsType);
		},
		getStatusColor(item) {
			return statuses.color(item.status);
		},
		getStatusTitle(item) {
			return this.$t(`status/${statuses.operational(item.status)}`);
		},
		getSelectedItems() {
			return this.$refs.grid.getSelectedItems();
		},
		resetSelectedRows() {
			return this.$refs.grid.resetSelectedRows();
		},
		setEditOrder(id) {
			let order = undefined;
			if (id) {
				order = this.orders.find(x => x.id === id);
			} else {
				let selectedItems = this.$refs.grid.getSelectedItems();
				if (selectedItems.length > 1) return;
				[order] = selectedItems;
			}
			if (!order) return;

			if (!order) return;
			eventBus.$emit('setEditOrder', order.orderId || order.id);
		},
		openViewAppMessages(appMessages) {
			this.appMessages = appMessages;
			this.showAppMessages = true;
		}
	}
};
</script>


<style lang="scss" scoped>
.order-list {
	.order-list-header {
		height: 100%;
		display:flex;
		align-items: center;
		padding-left: 15px;
	}
	.order-list-sub-header {
		height: 100%;
		.buttons-with-text {
			display:grid;
			padding: 0px 10px;
			height: 100%;
			align-items: center;
			grid-template-columns: repeat(2, minmax(100px, max-content)) auto repeat(2, minmax(100px, max-content));
			grid-column-gap: 10px;
		}
		.buttons-no-text {
			display:grid;
			padding: 0px 10px;
			height: 100%;
			align-items: center;
			grid-template-columns: repeat(2, minmax(min-content, max-content)) auto repeat(2, minmax(min-content, max-content));
			grid-column-gap: 10px;
		}
	}
	.order-list-content {
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0px 5px
	}
}
.missing-geo-data {
	background-color: yellow;
}
</style>
